import { useNavigate, useParams, Outlet, useSearchParams } from 'react-router-dom';
import { Box, Container, useTheme, IconButton, Typography, Alert, CircularProgress, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Loader from '../components/Loader';
import ErrorAlert from '../components/ErrorAlert';
import { useLoan } from "./LoanDetails/hooks/useLoan";
import { LoanProvider } from "./LoanDetails/LoanContext";
import type { Loan } from "./LoansDashboard";
import LoanDetailsTabs from "./LoanDetails/components/LoanDetailsTabs";
import { useState, useCallback, useEffect } from 'react';

const LoanDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { loanId } = useParams();
  const [searchParams] = useSearchParams();
  const { loan, loading, error, refetch } = useLoan(loanId);
  
  
  // Refresh trigger mechanism
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  const triggerRefresh = useCallback(() => {
    setIsRefreshing(true);
    setShowSuccessMessage(false);
    setRefreshTrigger(prev => prev + 1);
    refetch(); // Also refresh the main loan data
    
    // Show success message after refresh completes
    setTimeout(() => {
      setIsRefreshing(false);
      setShowSuccessMessage(true);
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    }, 2000);
  }, [refetch]);
  
  // Check if user is returning from a payment (simplified - no auto-refresh needed)
  useEffect(() => {
    const paymentSuccess = searchParams.get('paymentSuccess');
    const paymentIntentId = searchParams.get('paymentIntentId');
    
    if (paymentSuccess === 'true' || paymentIntentId) {
      
      // Clean up URL parameters
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('paymentSuccess');
      newSearchParams.delete('paymentIntentId');
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);


  if (loading) return <Loader />;
  if (error) return <ErrorAlert message={error} />;
  
  // Show loading overlay when refreshing after payment
  if (isRefreshing) {
    return (
      <div className="dashboard-bg">
        <Container
          maxWidth={false}
          sx={{
            width: '60%',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Box sx={{ textAlign: 'center', color: theme.palette.text.primary }}>
            <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Processing Payment...
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              Please wait while we update your loan information
            </Typography>
          </Box>
        </Container>
      </div>
    );
  }

  // Loan data is now properly structured from useLoan hook
  // The loan data is nested under key '0' because of how the data is structured
  const extractedLoanData = (loan as Loan | Loan[])?.[0] || loan;
  
  // Debug: Check if schedule data is being preserved
  
  // Preserve the schedule data from the main loan object
  const loanData = {
    ...extractedLoanData,
    schedule: loan?.schedule || []
  };
  

  
  
  return (
    <LoanProvider value={{ loan: loanData, loading, error, refetch, refreshTrigger, triggerRefresh }}>
      <div className="dashboard-bg">
        {/* Above section: original 60% width */}
        <Box sx={{ width: '60%', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ alignSelf: 'flex-start', width: '100%', mb: 2 }}>
            <IconButton onClick={() => navigate('/loans')} sx={{ color: theme.palette.text.primary, mb: 1 }}>
              <ArrowBackIcon fontSize="large" sx={{ color: '#fff' }} />
            </IconButton>
            {/* Header Row: Loan Name + Status Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, mt: 2 }}>
              <Typography variant="h6" sx={{ color: theme.palette.mode === 'dark' ? '#fff' : '#fff', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 400, fontSize: 22 }}>
                {loanData?.productName || loanData?.loanProductName || 'Business Loan'}
              </Typography>
              {loanData?.status && (
                <Box
                sx={{
                  background: loanData.status.includes('Bad') 
                    ? (theme.palette.mode === 'dark' ? '#3a2323' : '#fce8e6') 
                    : (theme.palette.mode === 'dark' ? '#173F2C' : '#e6f4ea'),
                  color: loanData.status.includes('Bad') 
                    ? (theme.palette.mode === 'dark' ? '#ff4d4f' : '#c62828') 
                    : (theme.palette.mode === 'dark' ? '#3DF06A' : '#1e8e3e'),
                  fontFamily: "'Libre Franklin', sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  borderRadius: 15,
                  px: 2.5,
                  py: 0.5,
                  ml: 1,
                  display: 'inline-block',
                }}
              >
                {loanData.status}
              </Box>              
              )}
            </Box>
            {/* Amount Row - Payoff amount as of today */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 0 }}>
              <Typography variant="h2" sx={{ color: theme.palette.mode === 'dark' ? '#fff' : '#FFF', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 56 }}>
                {loanData?.totalOutstandingAsOfDate !== undefined ? `£${Number(loanData.totalOutstandingAsOfDate).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
              </Typography>
              <Tooltip title="Payoff amount as of today">
                <InfoOutlinedIcon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? '#bdbdbd' : 'rgba(255,255,255,0.8)', cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            {/* Loan Amount Label Row */}
            <Typography variant="subtitle1" sx={{ color: theme.palette.mode === 'dark' ? '#bdbdbd' : '#FFF', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 18, mb: 2 }}>
              Original Loan Amount: {loanData?.loanAmount !== undefined ? `£${Number(loanData.loanAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
            </Typography>
            
            {/* Success message after payment processing */}
            {showSuccessMessage && (
              <Alert 
                severity="success" 
                icon={<CheckCircleIcon />}
                sx={{ 
                  mt: 2, 
                  bgcolor: 'rgba(76, 175, 80, 0.1)', 
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  '& .MuiAlert-message': {
                    color: theme.palette.text.primary
                  }
                }}
              >
                Payment processed successfully! Your loan information has been updated.
              </Alert>
            )}
          </Box>
        </Box>
        {/* Tabs: full width so all tabs fit in one view */}
        <Box sx={{ width: '100%', px: 2 }}>
          <LoanDetailsTabs />
        </Box>
        {/* Below section: original 60% width */}
        <Box sx={{ width: '60%', margin: '0 auto' }}>
          <Outlet />
        </Box>
      </div>
    </LoanProvider>
  );
};

export default LoanDetails; 